// useFirestoreQuery — Generic data-fetching hook with a 60-second
// module-level cache keyed by a stable cache key. Mutations that
// must invalidate a slice call `invalidateCache(key)`.
//
// The hook is intentionally bare-bones: no React Query, no
// background revalidation. Most admin views fetch a handful of
// documents, render once, and stay until the user navigates away
// or triggers a mutation. The 60s TTL stops the cache from going
// stale across long-lived sessions.

import { useEffect, useRef, useState } from 'react';

export const TTL_MS = 60 * 1000;

interface CacheEntry<T> {
  value: T;
  ts: number;
}

// Single module-level cache shared across every hook instance.
const cache = new Map<string, CacheEntry<unknown>>();

export function invalidateCache(prefix?: string): void {
  if (!prefix) {
    cache.clear();
    return;
  }
  for (const key of Array.from(cache.keys())) {
    if (key === prefix || key.startsWith(prefix + ':')) {
      cache.delete(key);
    }
  }
}

export interface QueryState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useFirestoreQuery<T>(
  cacheKey: string | null,
  fetcher: () => Promise<T>,
): QueryState<T> {
  const [data, setData] = useState<T | null>(() => {
    if (!cacheKey) return null;
    const cached = cache.get(cacheKey) as CacheEntry<T> | undefined;
    if (cached && Date.now() - cached.ts < TTL_MS) return cached.value;
    return null;
  });
  const [loading, setLoading] = useState<boolean>(data === null);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // The fetcher closure changes on every render in callers that
  // build queries inline. We pin the most recent one in a ref so
  // the effect's dep array can stay stable on cacheKey + reloadToken.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!cacheKey) return;
    const cached = cache.get(cacheKey) as CacheEntry<T> | undefined;
    if (cached && Date.now() - cached.ts < TTL_MS) {
      setData(cached.value);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcherRef
      .current()
      .then((value) => {
        if (cancelled) return;
        cache.set(cacheKey, { value: value as unknown, ts: Date.now() });
        setData(value);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cacheKey, reloadToken]);

  function refetch() {
    if (cacheKey) cache.delete(cacheKey);
    setReloadToken((n) => n + 1);
  }

  return { data, loading, error, refetch };
}
